/**
 * Намаление с таймер за отделна корица — една истина за админа, каталога,
 * чекаута и презаверката на поръчката.
 *
 * За разлика от „NEW" тага ([[coverNewBadge.js]]), тук периодът е с ЧАС, не
 * само дата: таймерът брои до конкретен момент и трябва да свърши тогава,
 * когато го е обявила промоцията.
 *
 * Цената е на ДЕСТИНАЦИЯ (`Destination.price`), а намалението е на КОРИЦА.
 * Затова процентът се прилага върху базовата цена на дестинацията, а
 * допълнителните листове се таксуват по пълна цена — намалена е книгата, не
 * добавките към нея.
 */

/** Над това няма смислена промоция; пази и от изтървана нула в админа. */
export const MAX_DISCOUNT_PERCENT = 90;
const MIN_DISCOUNT_PERCENT = 1;
const CENTS = 100;
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MS_PER_MINUTE = MS_PER_SECOND * SECONDS_PER_MINUTE;
const MS_PER_HOUR = MS_PER_MINUTE * MINUTES_PER_HOUR;
const MS_PER_DAY = MS_PER_HOUR * HOURS_PER_DAY;

/**
 * Момент в милисекунди от Date/ISO низ, или `null`.
 * @param {unknown} value
 * @returns {number | null}
 */
export function toDiscountTime(value) {
  if (!value) {
    return null;
  }
  const d = value instanceof Date ? value : new Date(String(value));
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Процент за запис в базата: цяло число 1..90 или `null`.
 * @param {unknown} value
 * @returns {number | null}
 */
export function normalizeDiscountPercent(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < MIN_DISCOUNT_PERCENT || n > MAX_DISCOUNT_PERCENT) {
    return null;
  }
  return n;
}

/**
 * Стойност за `DateTime` колона: `Date` или `null`.
 * @param {unknown} value
 * @returns {Date | null}
 */
export function coverDiscountDateForDb(value) {
  const ms = toDiscountTime(value);
  return ms == null ? null : new Date(ms);
}

/**
 * `<input type="datetime-local">` иска локално време без зона: `YYYY-MM-DDTHH:mm`.
 * @param {unknown} value
 * @returns {string}
 */
export function toDiscountLocalInput(value) {
  const ms = toDiscountTime(value);
  if (ms == null) {
    return '';
  }
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Активното намаление в проценти, или 0. Проверява и трите условия наведнъж:
 * включено ли е, има ли валиден процент и в периода ли сме.
 *
 * @param {{ isDiscounted?: boolean, discountPercent?: unknown,
 *   discountFrom?: unknown, discountUntil?: unknown } | null | undefined} template
 * @param {number} [nowMs]
 * @returns {number} 0 = няма намаление
 */
export function coverDiscountPercent(template, nowMs = Date.now()) {
  if (!template?.isDiscounted) {
    return 0;
  }
  const percent = normalizeDiscountPercent(template.discountPercent);
  if (percent == null) {
    return 0;
  }
  const from = toDiscountTime(template.discountFrom);
  if (from != null && nowMs < from) {
    return 0;
  }
  const until = toDiscountTime(template.discountUntil);
  if (until != null && nowMs >= until) {
    return 0;
  }
  return percent;
}

/**
 * Краят на намалението в милисекунди, ако тече точно сега и има край.
 * `null` при безсрочна промоция — тогава таймер няма какво да брои.
 *
 * @param {object | null | undefined} template
 * @param {number} [nowMs]
 * @returns {number | null}
 */
export function coverDiscountEndsAt(template, nowMs = Date.now()) {
  if (coverDiscountPercent(template, nowMs) === 0) {
    return null;
  }
  return toDiscountTime(template?.discountUntil);
}

/**
 * Цена след намаление, закръглена до стотинка. Процент 0 връща цената непокътната
 * (същото число, не 0), за да може да се вика безусловно.
 *
 * @param {unknown} price
 * @param {unknown} percent
 * @returns {number}
 */
export function applyCoverDiscount(price, percent) {
  const value = Number(price);
  if (!Number.isFinite(value) || value <= 0) {
    return Number.isFinite(value) ? value : 0;
  }
  const p = normalizeDiscountPercent(percent);
  if (p == null) {
    return value;
  }
  return Math.round(value * (CENTS - p)) / CENTS;
}

/**
 * Оставащото време, разбито за таймера. Отрицателното се нулира.
 *
 * @param {number} msLeft
 * @returns {{ days: number, hours: number, minutes: number, seconds: number, total: number }}
 */
export function splitCountdown(msLeft) {
  const total = Math.max(0, Math.floor(Number(msLeft) || 0));
  return {
    total,
    days: Math.floor(total / MS_PER_DAY),
    hours: Math.floor((total % MS_PER_DAY) / MS_PER_HOUR),
    minutes: Math.floor((total % MS_PER_HOUR) / MS_PER_MINUTE),
    seconds: Math.floor((total % MS_PER_MINUTE) / MS_PER_SECOND),
  };
}

/**
 * Таймерът като текст. Над един ден се показват дни и часове — секундите там са
 * шум; под един ден се брои до секунда, защото точно тогава има значение.
 *
 * @param {number} msLeft
 * @returns {string}
 */
export function formatCountdown(msLeft) {
  const { days, hours, minutes, seconds } = splitCountdown(msLeft);
  const pad = (n) => String(n).padStart(2, '0');
  if (days > 0) {
    return `${days}д ${pad(hours)}:${pad(minutes)}`;
  }
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}
